import { useState, useCallback, useMemo } from 'react'
import { AlertCircle, Clock } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import { LoadingTable } from '../ui/loading-states'
import { LatenessFilters } from './LatenessFilters'
import { LatenessGrid } from './LatenessTable'
import { useTimesheetReport } from '../../hooks/useReports'
import { useOrganizationsForSelect } from '../../hooks/organizations/useOrganizations'
import { useDepartmentsForSelect } from '../../hooks/useDepartments'
import { exportLatenessToExcel } from '../../utils/exportLatenessToExcel'
import type { LatenessReportFilters, TimesheetReportFilters } from '../../types/reports.types'

export function LatenessReport() {
  const today = new Date()
  const [filters, setFilters] = useState<LatenessReportFilters>({
    year: today.getFullYear(),
    month: today.getMonth() + 1,
  })
  const [shouldFetch, setShouldFetch] = useState(false)

  const { data: organizations } = useOrganizationsForSelect()
  const { data: departments } = useDepartmentsForSelect(filters.organizationId || null)

  const timesheetFilters: TimesheetReportFilters = {
    year: filters.year,
    month: filters.month,
    organizationId: filters.organizationId,
    departmentId: filters.departmentId,
  }

  const {
    data: report,
    isLoading,
    error,
    isFetching,
  } = useTimesheetReport(timesheetFilters, shouldFetch)

  const filteredEmployees = useMemo(() => {
    if (!report) return []
    if (filters.onlyLate) {
      return report.employees.filter((e) => e.totals.late_count > 0)
    }
    return report.employees
  }, [report, filters.onlyLate])

  const lateEmployeesCount = useMemo(() => {
    if (!report) return 0
    return report.employees.filter((e) => e.totals.late_count > 0).length
  }, [report])

  const handleFiltersChange = (newFilters: Partial<LatenessReportFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }))
  }

  const handleGenerateReport = () => {
    setShouldFetch(true)
  }

  const handleExport = useCallback(async () => {
    if (report && filteredEmployees.length > 0) {
      try {
        await exportLatenessToExcel(filteredEmployees, report.meta)
      } catch (err) {
        console.error('Export failed:', err)
      }
    }
  }, [report, filteredEmployees])

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <LatenessFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onGenerateReport={handleGenerateReport}
            onExport={handleExport}
            canExport={!!(report && filteredEmployees.length > 0)}
            organizations={organizations || []}
            departments={departments || []}
            isLoading={isLoading || isFetching}
          />
        </CardContent>
      </Card>

      {/* Error state */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>Ошибка загрузки отчета. Попробуйте еще раз.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {shouldFetch && (
        <Card className="flex flex-col" style={{ height: 'calc(100vh - 380px)', minHeight: '400px' }}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Отчет по опозданиям
            </CardTitle>
            <CardDescription>
              {report ? (
                <>
                  {report.meta.month_name} {report.meta.year} |{' '}
                  Сотрудников: {filteredEmployees.length} |{' '}
                  С опозданиями: {lateEmployeesCount}
                </>
              ) : (
                'Загрузка...'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col overflow-hidden pb-0">
            {isLoading || isFetching ? (
              <LoadingTable rows={10} />
            ) : filteredEmployees.length > 0 ? (
              <LatenessGrid employees={filteredEmployees} meta={report!.meta} />
            ) : (
              <div className="text-center py-8">
                <Clock className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                  Опоздавших не найдено
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {filters.onlyLate
                    ? 'За выбранный период ни один сотрудник не опоздал.'
                    : 'Нет сотрудников для отображения.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Initial state */}
      {!shouldFetch && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Clock className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Выберите период и нажмите "Сформировать отчет"
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Отчет покажет опоздания сотрудников за каждый день выбранного месяца.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
